import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import type { InputType } from 'config'
import { useEffect, useState } from 'react'
import { getAiProjects } from 'services/apis/aiProjects'
import { submitAiPromptTemplate } from 'services/apis/conditions'
import { svgLoading } from 'stories/assets'
import { Button } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { aiPromptTemplateInputs } from './constants'
import { AiPromptTemplate } from './type'

interface AiPromptTemplateDetailsProps {
  defaults: AiPromptTemplate | null
  onBack: Function
  onComplete: Function
}

export function AiPromptTemplateDetails({ defaults, onBack, onComplete }: AiPromptTemplateDetailsProps) {
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>({})

  useEffect(() => {
    Promise.all([getAiProjects()])
      .then(([projects]) => {
        const nextInputs = cloneDeep(aiPromptTemplateInputs())
        const projectOptions: Record<number, string> = {}
        ;(projects || []).forEach((project) => {
          projectOptions[project.id] = project.name
        })
        nextInputs.projectId.options = projectOptions

        if (defaults) {
          Object.keys(nextInputs).forEach((key) => {
            nextInputs[key].value = (defaults as any)[key]
          })
        }
        setInputs(nextInputs)
      })
      .catch(() => setInputs(aiPromptTemplateInputs()))
  }, [defaults])

  const onChange = (key: string, value: unknown) => {
    const next = cloneDeep(inputs)
    next[key].value = InputConvert(next[key], value)
    next[key].error = ''
    setInputs(next)
  }

  const onSubmit = async () => {
    const nextInputs = cloneDeep(inputs)
    let hasError = false
    const values: Record<string, any> = {}
    Object.keys(nextInputs).forEach((key) => {
      nextInputs[key].error = InputValidate(nextInputs[key])
      if (nextInputs[key].error) hasError = true
      values[key] = InputConvert(nextInputs[key], nextInputs[key].value)
    })
    setInputs(nextInputs)
    if (hasError) return

    setLoading(true)
    try {
      await submitAiPromptTemplate(defaults?.id || 0, {
        ...values,
        projectId: values.projectId ? Number(values.projectId) : undefined,
      })
      onComplete()
      onBack()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
          <img src={svgLoading} alt="Loading" className="w-10 h-10" />
        </div>
      )}
      <div className="text-shade-blue flex items-center mb-4 font-bold cursor-pointer" onClick={() => onBack()}>
        <ArrowLeftIcon className="w-4 h-4" />
        <span className="hover:underline ml-1">Back</span>
      </div>
      <h2 className="text-2xl font-bold mb-4">{defaults ? 'Edit AI Prompt Template' : 'Add AI Prompt Template'}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {Object.keys(inputs).map((key) => (
          <div className={inputs[key].span ? `col-span-${inputs[key].span}` : ''} key={key}>
            <RenderInput input={inputs[key]} Key={key} onChange={onChange} />
          </div>
        ))}
      </div>
      <Button onClick={onSubmit}>Save</Button>
    </div>
  )
}
